import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Chip, CircularProgress, Link, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { AuthService } from '../../services/auth.service';
import { ExportFinanceService } from '../../services/exportFinance.service';
import { ExportFinanceSummary } from '../../interfaces/exportFinance.interface';
import { useToast } from '../../context/ToastContext';
import { formatDate } from '../../utils/dateUtils';
import { formatMoney, humanizeEnum } from '../../utils/format';

/** Export finance (W3-B8): packing credit per export LC and B2B acceptances retired by loans. */
const ExportFinance: React.FC = () => {
  const toast = useToast();
  const institutionId = AuthService.getSelectedInstitution() ?? '';
  const [data, setData] = useState<ExportFinanceSummary | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!institutionId) return;
    try {
      setLoading(true);
      setData(await ExportFinanceService.summary(institutionId));
    } catch (err) {
      toast.showApiError(err, 'Could not load export finance');
    } finally {
      setLoading(false);
    }
  }, [institutionId, toast]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  return (
    <Box data-help-page="financing.exportFinance.page" sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>Export finance</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} data-testid="ef-totals">
        As of {formatDate(data.asOf)}: packing credit outstanding {formatMoney(data.totalPcOutstanding, 'BDT')};
        B2B retirement loans outstanding {formatMoney(data.totalRetirementLoansOutstanding, 'BDT')}.
      </Typography>

      <Paper data-help-section="financing.exportFinance.pc" sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Packing credit by export LC</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell data-help="financing.exportFinance.pc.col.lc">Export LC</TableCell><TableCell data-help="financing.exportFinance.pc.col.buyer">Buyer</TableCell>
                <TableCell data-help="financing.exportFinance.pc.col.base" align="right">PC base</TableCell><TableCell data-help="financing.exportFinance.pc.col.eligibility" align="right">Eligibility (BDT)</TableCell>
                <TableCell data-help="financing.exportFinance.pc.col.outstanding" align="right">Outstanding</TableCell><TableCell data-help="financing.exportFinance.pc.col.liquidated" align="right">Liquidated</TableCell>
                <TableCell data-help="financing.exportFinance.pc.col.headroom" align="right">Headroom</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.packingCredit.map(p => (
                <TableRow key={p.exportLcId} hover>
                  <TableCell><Link data-help="financing.exportFinance.action.openLc" component={RouterLink} to={`/commercial/lc/${p.exportLcId}`}>{p.lcNumber}</Link></TableCell>
                  <TableCell>{p.buyerName}</TableCell>
                  <TableCell align="right">{formatMoney(p.pcBaseForeign, p.currencyCode)}</TableCell>
                  <TableCell align="right">{formatMoney(p.eligibilityBase, 'BDT')}</TableCell>
                  <TableCell align="right">{formatMoney(p.outstanding, 'BDT')}</TableCell>
                  <TableCell align="right">{formatMoney(p.liquidated, 'BDT')}</TableCell>
                  <TableCell align="right">{formatMoney(p.headroom, 'BDT')}</TableCell>
                </TableRow>
              ))}
              {data.packingCredit.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center">No packing credit drawn. Open a PACKING_CREDIT facility and draw against an export LC.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {data.packingCredit.flatMap(p => p.warnings.map(w => <Alert key={`${p.exportLcId}${w}`} severity="warning" sx={{ mt: 1 }}>{p.lcNumber}: {w}</Alert>))}
      </Paper>

      <Paper data-help-section="financing.exportFinance.retired" sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>B2B acceptances retired by loans</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell data-help="financing.exportFinance.b2b.col.lc">B2B LC</TableCell><TableCell data-help="financing.exportFinance.b2b.col.bill">Bill</TableCell><TableCell data-help="financing.exportFinance.b2b.col.loan">Loan</TableCell><TableCell data-help="financing.exportFinance.b2b.col.facility">Facility</TableCell>
                <TableCell data-help="financing.exportFinance.b2b.col.loanDate">Loan date</TableCell><TableCell data-help="financing.exportFinance.b2b.col.maturity">Maturity</TableCell>
                <TableCell data-help="financing.exportFinance.b2b.col.amount" align="right">Loan amount</TableCell><TableCell data-help="financing.exportFinance.b2b.col.outstanding" align="right">Outstanding</TableCell><TableCell data-help="financing.exportFinance.b2b.col.status">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.retiredAcceptances.map(r => (
                <TableRow key={r.drawdownId} hover>
                  <TableCell><Link data-help="financing.exportFinance.action.openB2b" component={RouterLink} to={`/commercial/back-to-back-lc/${r.masterLcId}/${r.b2bId}`}>{r.b2bLcNumber}</Link></TableCell>
                  <TableCell>{r.billReference}</TableCell>
                  <TableCell>{r.drawdownNo}</TableCell>
                  <TableCell><Link data-help="financing.exportFinance.action.openFacility" component={RouterLink} to={`/financing/facilities/${r.facilityId}`}>{r.facilityNo}</Link> ({humanizeEnum(r.facilityType)})</TableCell>
                  <TableCell>{formatDate(r.loanDate)}</TableCell>
                  <TableCell>{r.loanMaturity ? formatDate(r.loanMaturity) : '—'}</TableCell>
                  <TableCell align="right">{formatMoney(r.loanAmount, 'BDT')}</TableCell>
                  <TableCell align="right">{formatMoney(r.loanOutstanding, 'BDT')}</TableCell>
                  <TableCell><Chip size="small" label={r.loanStatus} /></TableCell>
                </TableRow>
              ))}
              {data.retiredAcceptances.length === 0 && (
                <TableRow><TableCell colSpan={9} align="center">No acceptance has been retired by a loan. Use "Retire by loan" on the B2B maturity schedule.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default ExportFinance;
